import { useState, useEffect, useMemo } from 'react'
import {
  Box,
  Button,
  Flex,
  Input,
  Text,
  Textarea,
  Grid,
  GridItem,
  DialogRoot,
  DialogBackdrop,
  DialogPositioner,
  DialogContent,
  DialogCloseTrigger,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  Toaster,
  createToaster,
  Alert,
} from '@chakra-ui/react'
import { NativeSelectRoot, NativeSelectField, Switch } from '@chakra-ui/react'
import { Calendar, ChevronDown, Video, AlertTriangle } from 'lucide-react'
import { Field } from '@/components/ui/field'
import { useProperties } from '@/features/properties/hooks/useProperties'
import { useCreateMeeting, useAllMeetings } from '../hooks'
import type { MeetingType } from '../types'
import { MEETING_TYPE_LABELS } from '../types'

const toaster = createToaster({
  placement: 'top-end',
  pauseOnPageIdle: true,
})

export interface MeetingCreateModalProps {
  isOpen: boolean
  onClose: () => void
  residenceId?: string // Optionnel - si fourni, présélectionne la résidence
}

interface FormData {
  residence_id: string
  title: string
  meeting_type: MeetingType
  meeting_date: string
  start_time: string
  location: string
  notes: string
  video_enabled: boolean
}

const initialFormData: FormData = {
  residence_id: '',
  title: '',
  meeting_type: 'ordinary',
  meeting_date: '',
  start_time: '18:00',
  location: '',
  notes: '',
  video_enabled: false,
}

export function MeetingCreateModal({ isOpen, onClose, residenceId }: MeetingCreateModalProps) {
  const { data: properties = [] } = useProperties()
  const [formData, setFormData] = useState<FormData>({
    ...initialFormData,
    residence_id: residenceId || '',
  })
  const createMeeting = useCreateMeeting()

  // Récupérer les AG existantes pour la résidence sélectionnée
  const { data: existingMeetings = [] } = useAllMeetings({
    residence_id: formData.residence_id || undefined,
    includePast: true,
  })

  // Vérifier s'il existe déjà une AG ordinaire pour cette résidence et cette année
  const existingOrdinaryMeeting = useMemo(() => {
    if (!formData.residence_id || !formData.meeting_date || formData.meeting_type !== 'ordinary') {
      return null
    }

    const selectedYear = new Date(formData.meeting_date).getFullYear()

    return existingMeetings.find((meeting) => {
      if (meeting.meeting_type !== 'ordinary') return false
      const meetingYear = new Date(meeting.meeting_date).getFullYear()
      return meetingYear === selectedYear
    })
  }, [formData.residence_id, formData.meeting_date, formData.meeting_type, existingMeetings])

  const hasOrdinaryConflict = !!existingOrdinaryMeeting

  // Mettre à jour residence_id si fourni en prop
  useEffect(() => {
    if (residenceId) {
      setFormData(prev => ({ ...prev, residence_id: residenceId }))
    }
  }, [residenceId])

  const handleClose = () => {
    setFormData({
      ...initialFormData,
      residence_id: residenceId || '',
    })
    onClose()
  }

  const handleChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  const handleSubmit = async () => {
    if (!formData.residence_id) {
      toaster.create({
        title: 'Champ requis',
        description: 'Veuillez sélectionner une résidence',
        type: 'error',
      })
      return
    }

    if (!formData.title || !formData.meeting_date) {
      toaster.create({
        title: 'Champs requis',
        description: 'Veuillez remplir le titre et la date de l\'AG',
        type: 'error',
      })
      return
    }

    try {
      // Note: Cast temporaire - video_enabled sera reconnu après la mise à jour des types Supabase
      await createMeeting.mutateAsync({
        residence_id: formData.residence_id,
        title: formData.title,
        meeting_type: formData.meeting_type,
        meeting_date: formData.meeting_date,
        start_time: formData.start_time,
        location: formData.location || null,
        notes: formData.notes || null,
        status: 'SCHEDULED',
        video_enabled: formData.video_enabled,
      } as any)

      toaster.create({
        title: 'AG créée',
        description: 'L\'assemblée générale a été créée avec succès',
        type: 'success',
      })

      handleClose()
    } catch (error) {
      console.error('Error creating meeting:', error)
      toaster.create({
        title: 'Erreur',
        description: 'Impossible de créer l\'assemblée générale',
        type: 'error',
      })
    }
  }

  // Générer un titre par défaut basé sur le type et la date
  const generateDefaultTitle = () => {
    if (!formData.meeting_date) return

    const date = new Date(formData.meeting_date)
    const year = date.getFullYear()
    const typeLabel = MEETING_TYPE_LABELS[formData.meeting_type]

    setFormData((prev) => ({
      ...prev,
      title: `AG ${typeLabel} ${year}`,
    }))
  }

  return (
    <>
      <Toaster toaster={toaster}>
        {() => null}
      </Toaster>
      <DialogRoot open={isOpen} onOpenChange={(e) => !e.open && handleClose()} size="lg">
        <DialogBackdrop />
        <DialogPositioner>
        <DialogContent maxW="600px">
          <DialogCloseTrigger />
          <DialogHeader>
            <DialogTitle>
              <Flex align="center" gap={2}>
                <Calendar size={20} />
                Nouvelle Assemblée Générale
              </Flex>
            </DialogTitle>
          </DialogHeader>

          <DialogBody>
            <Grid templateColumns="repeat(2, 1fr)" gap={4}>
              {/* Résidence */}
              <GridItem colSpan={2}>
                <Field label="Résidence" required>
                  <Box position="relative">
                    <NativeSelectRoot>
                      <NativeSelectField
                        value={formData.residence_id}
                        onChange={(e) => handleChange('residence_id', e.target.value)}
                      >
                        <option value="">Sélectionnez une résidence</option>
                        {properties.map((property) => (
                          <option key={property.id} value={property.id}>
                            {property.residence_key
                              ? `${property.residence_key} - ${property.name}`
                              : property.name}
                          </option>
                        ))}
                      </NativeSelectField>
                    </NativeSelectRoot>
                    <Box
                      position="absolute"
                      right="3"
                      top="50%"
                      transform="translateY(-50%)"
                      pointerEvents="none"
                      color="gray.500"
                    >
                      <ChevronDown size={16} />
                    </Box>
                  </Box>
                </Field>
              </GridItem>

              {/* Type d'AG */}
              <GridItem colSpan={1}>
                <Field label="Type d'AG" required>
                  <Box position="relative">
                    <NativeSelectRoot>
                      <NativeSelectField
                        value={formData.meeting_type}
                        onChange={(e) => handleChange('meeting_type', e.target.value)}
                      >
                        {Object.entries(MEETING_TYPE_LABELS).map(([value, label]) => (
                          <option key={value} value={value}>
                            {label}
                          </option>
                        ))}
                      </NativeSelectField>
                    </NativeSelectRoot>
                    <Box
                      position="absolute"
                      right="3"
                      top="50%"
                      transform="translateY(-50%)"
                      pointerEvents="none"
                      color="gray.500"
                    >
                      <ChevronDown size={16} />
                    </Box>
                  </Box>
                </Field>
              </GridItem>

              {/* Date */}
              <GridItem colSpan={1}>
                <Field label="Date de l'AG" required>
                  <Input
                    type="date"
                    value={formData.meeting_date}
                    onChange={(e) => handleChange('meeting_date', e.target.value)}
                    onBlur={() => !formData.title && generateDefaultTitle()}
                  />
                </Field>
              </GridItem>

              {/* Titre */}
              <GridItem colSpan={2}>
                <Field label="Titre" required>
                  <Input
                    placeholder="Ex: AG Ordinaire 2024"
                    value={formData.title}
                    onChange={(e) => handleChange('title', e.target.value)}
                  />
                </Field>
              </GridItem>

              {/* Heure */}
              <GridItem colSpan={1}>
                <Field label="Heure de début">
                  <Input
                    type="time"
                    value={formData.start_time}
                    onChange={(e) => handleChange('start_time', e.target.value)}
                  />
                </Field>
              </GridItem>

              {/* Lieu */}
              <GridItem colSpan={1}>
                <Field label="Lieu">
                  <Input
                    placeholder="Salle de réunion..."
                    value={formData.location}
                    onChange={(e) => handleChange('location', e.target.value)}
                  />
                </Field>
              </GridItem>

              {/* Visioconférence */}
              <GridItem colSpan={2}>
                <Box
                  p={4}
                  borderRadius="md"
                  borderWidth="1px"
                  borderColor={formData.video_enabled ? 'brand.200' : 'gray.200'}
                  bg={formData.video_enabled ? 'brand.50' : 'transparent'}
                  transition="all 0.2s"
                >
                  <Flex align="center" justify="space-between">
                    <Flex align="center" gap={3}>
                      <Box
                        p={2}
                        borderRadius="md"
                        bg={formData.video_enabled ? 'brand.100' : 'gray.100'}
                      >
                        <Video size={20} color={formData.video_enabled ? '#065F46' : '#6B7280'} />
                      </Box>
                      <Box>
                        <Text fontWeight="medium" fontSize="sm">
                          Activer la visioconférence
                        </Text>
                        <Text fontSize="xs" color="gray.500">
                          Permet aux copropriétaires de participer à distance
                        </Text>
                      </Box>
                    </Flex>
                    <Switch.Root
                      checked={formData.video_enabled}
                      onCheckedChange={(details) =>
                        setFormData((prev) => ({ ...prev, video_enabled: details.checked }))
                      }
                      colorPalette="brand"
                    >
                      <Switch.HiddenInput />
                      <Switch.Control>
                        <Switch.Thumb />
                      </Switch.Control>
                    </Switch.Root>
                  </Flex>
                  {formData.video_enabled && (
                    <Box mt={3} pt={3} borderTopWidth="1px" borderColor="brand.200">
                      <Text fontSize="xs" color="brand.700">
                        Un lien de visioconférence sera automatiquement généré et inclus dans les convocations.
                        L'accès sera ouvert 30 minutes avant le début de l'AG.
                      </Text>
                    </Box>
                  )}
                </Box>
              </GridItem>

              {/* Notes */}
              <GridItem colSpan={2}>
                <Field label="Notes">
                  <Textarea
                    placeholder="Informations complémentaires..."
                    value={formData.notes}
                    onChange={(e) => handleChange('notes', e.target.value)}
                    rows={3}
                  />
                </Field>
              </GridItem>
            </Grid>

            {/* Alerte si AG ordinaire existe déjà */}
            {hasOrdinaryConflict && existingOrdinaryMeeting && (
              <Alert.Root status="warning" mt={4} borderRadius="md">
                <Alert.Indicator>
                  <AlertTriangle size={16} />
                </Alert.Indicator>
                <Alert.Content>
                  <Alert.Title fontSize="sm">AG Ordinaire déjà existante</Alert.Title>
                  <Alert.Description fontSize="xs">
                    Une AG Ordinaire existe déjà pour cette résidence en {new Date(formData.meeting_date).getFullYear()} :
                    "{existingOrdinaryMeeting.title}" du {new Date(existingOrdinaryMeeting.meeting_date).toLocaleDateString('fr-FR')}.
                    Il ne peut y avoir qu'une seule AG Ordinaire par résidence et par an.
                  </Alert.Description>
                </Alert.Content>
              </Alert.Root>
            )}

            {/* Info */}
            {!hasOrdinaryConflict && (
              <Box mt={4} p={3} bg="blue.50" borderRadius="md">
                <Text fontSize="sm" color="blue.700">
                  L'AG sera créée avec le statut "Planifiée". Vous pourrez ensuite ajouter les résolutions
                  et envoyer les convocations.
                </Text>
              </Box>
            )}
          </DialogBody>

          <DialogFooter>
            <Flex gap={2}>
              <Button variant="ghost" onClick={handleClose}>
                Annuler
              </Button>
              <Button
                colorPalette="brand"
                onClick={handleSubmit}
                loading={createMeeting.isPending}
                disabled={hasOrdinaryConflict}
              >
                Créer l'AG
              </Button>
            </Flex>
          </DialogFooter>
        </DialogContent>
        </DialogPositioner>
      </DialogRoot>
    </>
  )
}
